import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    IconButton,
    Tooltip,
    Typography,
    Toolbar,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
    CircularProgress,
    useTheme,
    useMediaQuery,
    TextField,
    InputAdornment,
    Collapse,
    FormControlLabel,
    Switch
} from '@mui/material';
import {
    Add as AddIcon,
    ViewColumn as ViewColumnIcon,
    DragIndicator as DragIndicatorIcon,
    Edit as EditIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    FilterList as FilterListIcon
} from '@mui/icons-material';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    horizontalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useListView } from '../hooks/useListView';
import { formatCellValue } from '../utils/dateFormatter';
import { getClickableRowStyles, getTableCellStyles, getTableHeaderCellStyles } from '../utils/tableUtils';
import type { Record, SortDirection } from '../types/form';
import { FormLayoutEditor } from './FormLayoutEditor/FormLayoutEditor';

// ============================================================================
// Interfaces
// ============================================================================
interface SortableHeaderProps {
    id: string;
    label: string;
    active: boolean;
    direction: 'asc' | 'desc';
    onSort: () => void;
}

// ============================================================================
// Helper Components
// ============================================================================
function SortableTableHeader({ id, label, active, direction, onSort }: SortableHeaderProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: 'grab',
        display: 'flex',
        alignItems: 'center',
        userSelect: 'none' as const
    };

    return (
        <Box ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <DragIndicatorIcon sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} />
            <TableSortLabel
                active={active}
                direction={direction}
                onClick={(e) => {
                    e.stopPropagation();
                    onSort();
                }}
            >
                {label}
            </TableSortLabel>
        </Box>
    );
}

// ============================================================================
// Main Component
// ============================================================================

interface ListViewProps {
    tableName?: string;
}

export default function ListView({ tableName: propTableName }: ListViewProps = {}) {
    const { tableName: urlTableName = '' } = useParams();
    const tableName = propTableName || urlTableName;
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [orderBy, setOrderBy] = useState<string | null>(null);
    const [order, setOrder] = useState<SortDirection>('asc');
    const [columnMenuAnchor, setColumnMenuAnchor] = useState<HTMLElement | null>(null);
    const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
    const [columnOrder, setColumnOrder] = useState<string[]>([]);
    const [hasInitializedColumns, setHasInitializedColumns] = useState(false);
    const [layoutEditorOpen, setLayoutEditorOpen] = useState(false);
    
    // Search functionality state
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [searchOpen, setSearchOpen] = useState<boolean>(false);
    
    // Main guest filter state (only for guests table)
    const [showMainGuestsOnly, setShowMainGuestsOnly] = useState<boolean>(false);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 10 }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    // Build filters object
    const filters = React.useMemo(() => {
        const filterObj: { [key: string]: any } = {};
        
        // Add main guest filter for guests table
        if (tableName === 'guests' && showMainGuestsOnly) {
            filterObj.is_main_guest = true;
        }
        
        return filterObj;
    }, [tableName, showMainGuestsOnly]);

    const {
        viewConfig,
        records,
        isLoading,
        error,
        saveViewConfig
    } = useListView(tableName, undefined, filters);

    // Reset hasInitializedColumns when tableName changes
    useEffect(() => {
        setHasInitializedColumns(false);
    }, [tableName]);

    useEffect(() => {
        if (!viewConfig?.fields || hasInitializedColumns) return;

        const savedVisibleFields = viewConfig.fields
            .filter(field => field.visible)
            .map(field => field.field_name);

        setVisibleColumns(savedVisibleFields);

        if (Array.isArray(viewConfig.columnOrder)) {
            const validColumnOrder = viewConfig.columnOrder.filter(field =>
                savedVisibleFields.includes(field)
            );
            const missing = savedVisibleFields.filter(field =>
                !validColumnOrder.includes(field)
            );
            setColumnOrder([...validColumnOrder, ...missing]);
        } else {
            setColumnOrder(savedVisibleFields);
        }

        setHasInitializedColumns(true);
    }, [viewConfig, hasInitializedColumns, tableName]);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = columnOrder.indexOf(active.id as string);
            const newIndex = columnOrder.indexOf(over.id as string);

            const newColumnOrder = arrayMove(columnOrder, oldIndex, newIndex);
            setColumnOrder(newColumnOrder);

            if (viewConfig) {
                const newConfig = {
                    ...viewConfig,
                    columnOrder: newColumnOrder,
                    fields: viewConfig.fields.map(field => ({
                        ...field,
                        visible: visibleColumns.includes(field.field_name)
                    })),
                    layout_config: viewConfig.layout_config
                };
                saveViewConfig.mutate(newConfig);
            }
        }
    };

    const handleSort = (field: string) => {
        const isAsc = orderBy === field && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(field);
    };

    const handleColumnMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setColumnMenuAnchor(event.currentTarget);
    };

    const handleColumnMenuClose = () => {
        setColumnMenuAnchor(null);
    };

    const handleColumnToggle = (field: string) => {
        const newVisibleColumns = visibleColumns.includes(field)
            ? visibleColumns.filter(col => col !== field)
            : [...visibleColumns, field];

        setVisibleColumns(newVisibleColumns);

        if (viewConfig) {
            const newConfig = {
                ...viewConfig,
                fields: viewConfig.fields.map(f => ({
                    ...f,
                    visible: newVisibleColumns.includes(f.field_name)
                })),
                layout_config: viewConfig.layout_config
            };
            saveViewConfig.mutate(newConfig);
        }
    };

    const handleNewRecord = () => {
        navigate(`/${tableName}/new`);
    };

    const handleRowClick = (record: Record) => {
        if (record.id) {
            navigate(`/${tableName}/${record.id}`);
        }
    };

    // Search functionality handlers
    const handleSearchToggle = () => {
        setSearchOpen(!searchOpen);
        if (searchOpen && searchQuery) {
            // Clear search when closing
            setSearchQuery('');
        }
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
    };

    const handleSearchClear = () => {
        setSearchQuery('');
    };

    // Main guest filter handlers
    const handleMainGuestToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShowMainGuestsOnly(event.target.checked);
    };

    // Filter records based on search query
    const filteredRecords = records ? records.filter((record: Record) => {
        if (!searchQuery.trim()) return true;
        
        const query = searchQuery.toLowerCase();
        
        // Search across all visible fields
        return visibleColumns.some(fieldName => {
            const field = viewConfig?.fields.find(f => f.field_name === fieldName);
            if (!field) return false;
            
            const value = record[field.field_name];
            if (value === null || value === undefined) return false;
            
            // Convert value to string for searching
            let searchableValue = '';
            try {
                if (typeof value === 'object') {
                    searchableValue = JSON.stringify(value);
                } else {
                    searchableValue = String(value);
                }
                return searchableValue.toLowerCase().includes(query);
            } catch (error) {
                return false;
            }
        });
    }) : [];

    const title = tableName ? tableName.charAt(0).toUpperCase() + tableName.slice(1) : '';

    const sortedRecords = filteredRecords ? [...filteredRecords].sort((a: Record, b: Record) => {
        if (!orderBy) return 0;
        const aValue = a[orderBy];
        const bValue = b[orderBy];
        return order === 'asc' ? (aValue < bValue ? -1 : 1) : (bValue < aValue ? -1 : 1);
    }) : [];

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error">Error loading data: {(error as Error).message}</Typography>
            </Box>
        );
    }

    if (!viewConfig || !records) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>No data available</Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Paper sx={{ width: '100%', mb: 2 }}>
                <Toolbar 
                    sx={{ 
                        pl: { sm: 2 }, 
                        pr: { xs: 1, sm: 1 }, 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        minHeight: { xs: '56px', sm: '64px' },
                        flexWrap: 'wrap'
                    }}
                >
                    <Typography 
                        variant="h6" 
                        component="div" 
                        sx={{ 
                            flex: searchOpen && isMobile ? '1 1 100%' : '1 1 auto',
                            fontSize: isMobile ? '1.1rem' : '1.25rem',
                            mb: searchOpen && isMobile ? 1 : 0
                        }}
                    >
                        {title}
                        {(searchQuery || showMainGuestsOnly) && (
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    ml: 1, 
                                    color: 'text.secondary',
                                    fontSize: '0.75rem'
                                }}
                            >
                                ({sortedRecords.length} of {records?.length || 0} records
                                {showMainGuestsOnly && tableName === 'guests' ? ' - Main Guests Only' : ''})
                            </Typography>
                        )}
                    </Typography>
                    
                    <Box sx={{ 
                        display: 'flex', 
                        gap: isMobile ? 0.5 : 1,
                        order: searchOpen && isMobile ? 3 : 'initial',
                        width: searchOpen && isMobile ? '100%' : 'auto'
                    }}>
                        <Tooltip title={searchOpen ? "Close Search" : "Search Records"}>
                            <IconButton 
                                onClick={handleSearchToggle}
                                sx={{
                                    minWidth: isMobile ? '48px' : '40px',
                                    minHeight: isMobile ? '48px' : '40px',
                                    bgcolor: searchOpen ? 'primary.main' : 'transparent',
                                    color: searchOpen ? 'primary.contrastText' : 'inherit',
                                    '&:hover': {
                                        bgcolor: searchOpen ? 'primary.dark' : 'action.hover',
                                    },
                                }}
                            >
                                <SearchIcon />
                            </IconButton>
                        </Tooltip>
                        
                        {!isMobile && (
                            <>
                                <Tooltip title="Edit Layout">
                                    <IconButton 
                                        onClick={() => setLayoutEditorOpen(true)}
                                        sx={{
                                            minWidth: '40px',
                                            minHeight: '40px',
                                        }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="View Columns">
                                    <IconButton 
                                        onClick={handleColumnMenuOpen}
                                        sx={{
                                            minWidth: '40px',
                                            minHeight: '40px',
                                        }}
                                    >
                                        <ViewColumnIcon />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                        
                        <Tooltip title="Add New">
                            <IconButton 
                                onClick={handleNewRecord}
                                sx={{
                                    minWidth: isMobile ? '48px' : '40px',
                                    minHeight: isMobile ? '48px' : '40px',
                                    bgcolor: isMobile ? 'primary.main' : 'transparent',
                                    color: isMobile ? 'primary.contrastText' : 'inherit',
                                    '&:hover': {
                                        bgcolor: isMobile ? 'primary.dark' : 'action.hover',
                                    },
                                }}
                            >
                                <AddIcon />
                            </IconButton>
                        </Tooltip>
                        
                        {isMobile && (
                            <Tooltip title="More Options">
                                <IconButton 
                                    onClick={handleColumnMenuOpen}
                                    sx={{
                                        minWidth: '48px',
                                        minHeight: '48px',
                                    }}
                                >
                                    <FilterListIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Toolbar>

                {/* Search Input - Collapsible */}
                <Collapse in={searchOpen}>
                    <Box sx={{ 
                        px: 2, 
                        pb: 2,
                        pt: isMobile ? 0 : 1
                    }}>
                        <TextField
                            fullWidth
                            size={isMobile ? "medium" : "small"}
                            placeholder={`Search ${title.toLowerCase()}...`}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery && (
                                    <InputAdornment position="end">
                                        <Tooltip title="Clear Search">
                                            <IconButton
                                                size="small"
                                                onClick={handleSearchClear}
                                                edge="end"
                                            >
                                                <ClearIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: theme.palette.background.paper,
                                }
                            }}
                        />
                        {searchQuery && (
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    mt: 0.5, 
                                    color: 'text.secondary',
                                    display: 'block'
                                }}
                            >
                                Showing {sortedRecords.length} of {records?.length || 0} records
                            </Typography>
                        )}
                    </Box>
                </Collapse>

                {/* Main Guest Filter Toggle - Only for guests table */}
                {tableName === 'guests' && (
                    <Box sx={{ 
                        px: 2, 
                        pb: 1,
                        borderBottom: showMainGuestsOnly ? '1px solid' : 'none',
                        borderColor: 'divider'
                    }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showMainGuestsOnly}
                                    onChange={handleMainGuestToggle}
                                    size="small"
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="body2" color="text.secondary">
                                    Show main guests only
                                </Typography>
                            }
                            sx={{ 
                                m: 0,
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '0.875rem'
                                }
                            }}
                        />
                        {showMainGuestsOnly && (
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    ml: 4,
                                    color: 'text.secondary',
                                    display: 'block',
                                    fontSize: '0.75rem'
                                }}
                            >
                                Showing one record per couple (main guest only)
                            </Typography>
                        )}
                    </Box>
                )}

                <TableContainer sx={{ overflowX: 'auto' }}>
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
                            <Table sx={{ minWidth: isMobile ? 'max-content' : 750 }}>
                                <TableHead>
                                    <TableRow>
                                        {columnOrder
                                            .filter(fieldName => visibleColumns.includes(fieldName))
                                            .map(fieldName => {
                                                const field = viewConfig.fields.find(f => f.field_name === fieldName);
                                                if (!field) return null;
                                                return (
                                                                                        <TableCell 
                                        key={`${tableName}-header-${field.field_name}`}
                                        sx={getTableHeaderCellStyles(isMobile)}
                                    >
                                                        <SortableTableHeader
                                                            id={field.field_name}
                                                            label={field.label}
                                                            active={orderBy === field.field_name}
                                                            direction={orderBy === field.field_name ? order : 'asc'}
                                                            onSort={() => handleSort(field.field_name)}
                                                        />
                                                    </TableCell>
                                                );
                                            })}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedRecords && sortedRecords.length > 0 ? (
                                        sortedRecords.map((record, index) => (
                                                                        <TableRow
                                hover
                                key={record.id || index}
                                sx={getClickableRowStyles(theme)}
                                onClick={() => handleRowClick(record)}
                            >
                                                {columnOrder
                                                    .filter(fieldName => visibleColumns.includes(fieldName))
                                                    .map(fieldName => {
                                                        const field = viewConfig.fields.find(f => f.field_name === fieldName);
                                                        if (!field) return null;
                                                        
                                                        // Safety wrapper to ensure objects are never rendered directly
                                                        let cellValue;
                                                        try {
                                                            cellValue = formatCellValue(record[field.field_name], field);
                                                            
                                                            // Enhanced safety check to ensure valid ReactNode
                                                            if (cellValue === null || cellValue === undefined) {
                                                                cellValue = '';
                                                            } else if (typeof cellValue === 'object') {
                                                                // Objects (including arrays) cannot be rendered as React children
                                                                if (Array.isArray(cellValue)) {
                                                                    cellValue = `[Array with ${cellValue.length} items]`;
                                                                } else {
                                                                    cellValue = JSON.stringify(cellValue);
                                                                }
                                                            } else if (typeof cellValue === 'function') {
                                                                // Functions cannot be rendered as React children
                                                                cellValue = '[Function]';
                                                            } else if (typeof cellValue === 'symbol') {
                                                                // Symbols cannot be rendered as React children
                                                                cellValue = '[Symbol]';
                                                            } else {
                                                                // Ensure it's a string, number, or boolean (valid ReactNode types)
                                                                cellValue = String(cellValue);
                                                            }
                                                        } catch (error) {
                                                            cellValue = String(record[field.field_name] || '');
                                                        }
                                                        
                                                                                                return (
                                            <TableCell 
                                                key={`${tableName}-${record.id || index}-${field.id}-${field.field_name}`}
                                                sx={getTableCellStyles(isMobile)}
                                            >
                                                                {cellValue}
                                                            </TableCell>
                                                        );
                                                    })}
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={visibleColumns.length} align="center">
                                                {searchQuery ? (
                                                    <Box sx={{ py: 2 }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            No records found matching &quot;{searchQuery}&quot;
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Try different search terms or{' '}
                                                            <Box 
                                                                component="span" 
                                                                sx={{ 
                                                                    color: 'primary.main', 
                                                                    cursor: 'pointer',
                                                                    textDecoration: 'underline'
                                                                }}
                                                                onClick={handleSearchClear}
                                                            >
                                                                clear search
                                                            </Box>
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    "No records found"
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </SortableContext>
                    </DndContext>
                </TableContainer>
            </Paper>

            <Menu
                anchorEl={columnMenuAnchor}
                open={Boolean(columnMenuAnchor)}
                onClose={handleColumnMenuClose}
            >
                {viewConfig?.fields?.map(field => (
                    <MenuItem
                        key={field.field_name}
                        onClick={() => handleColumnToggle(field.field_name)}
                    >
                        <ListItemIcon>
                            <Checkbox
                                edge="start"
                                checked={visibleColumns.includes(field.field_name)}
                                tabIndex={-1}
                                disableRipple
                            />
                        </ListItemIcon>
                        <ListItemText primary={field.label} />
                    </MenuItem>
                )) || (
                    <MenuItem disabled>
                        <ListItemText primary="No fields available" />
                    </MenuItem>
                )}
            </Menu>

            <FormLayoutEditor
                open={layoutEditorOpen}
                onClose={() => setLayoutEditorOpen(false)}
                onSave={async (newLayoutConfig) => {
                    if (viewConfig) {
                        const updatedConfig = {
                            ...viewConfig,
                            layout_config: newLayoutConfig
                        };

                        await saveViewConfig.mutateAsync(updatedConfig);
                        setLayoutEditorOpen(false);
                    }
                }}
                currentLayout={viewConfig?.layout_config ? { 
                    id: 0, 
                    table_name: tableName, 
                    layout_config: viewConfig.layout_config 
                } : null}
                tableName={tableName}
                fields={viewConfig?.fields || []}
            />
        </Box>
    );
}
